/**
 * Tenant Subdomain Redirect Utility
 * 
 * Handles automatic redirection to tenant-specific subdomains after login
 * Updated: Force cache refresh
 */

import { logger } from './logger';

const API_BASE_URL = import.meta.env.VITE_API_URL || 'https://api.elcapit.com';
const BASE_DOMAIN = 'elcapit.com';

interface TenantInfo {
  tenantId: string;
  tenantSlug?: string;
  subdomainUrl?: string;
  provisioningStatus?: string;
}

/**
 * Validates that a tenant ID is a simple identifier, not a URL or path
 */
function isValidTenantId(tenantId: string): boolean {
  // A valid tenant ID should only contain alphanumeric chars, hyphens, and underscores
  // It must not look like a URL, hostname, or path
  return /^[a-zA-Z0-9_-]+$/.test(tenantId) && !tenantId.includes('.')
}

/**
 * Fetch tenant information from the API
 */
export async function fetchTenantInfo(tenantId: string, token: string): Promise<TenantInfo | null> {
  if (!isValidTenantId(tenantId)) {
    logger.warn(`Invalid tenant ID format, skipping fetch: ${tenantId}`)
    return null
  }

  try {
    // Try the tenant-specific endpoint (works for tenant_admin)
    const response = await fetch(`${API_BASE_URL}/tenant/info`, {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    });

    if (!response.ok) {
      logger.warn(`Failed to fetch tenant info: ${response.status}`);
      return null;
    }

    const data = await response.json();
    return data;
  } catch (error) {
    logger.error('Error fetching tenant info:', error);
    return null;
  }
}

/**
 * Check if we're currently on the main domain (not a subdomain)
 */
export function isOnMainDomain(): boolean {
  const hostname = window.location.hostname;
  
  // Check if we're on the main domain (elcapit.com or www.elcapit.com)
  // or localhost for development
  return (
    hostname === BASE_DOMAIN ||
    hostname === `www.${BASE_DOMAIN}` ||
    hostname === 'localhost' ||
    hostname === '127.0.0.1'
  );
}

/**
 * Check if subdomain redirect is enabled
 * Can be disabled via localStorage for testing
 */
export function isSubdomainRedirectEnabled(): boolean {
  // Allow disabling redirect for testing/debugging
  const disabled = localStorage.getItem('disableSubdomainRedirect');
  return disabled !== 'true';
}

/**
 * Map tenant ID to subdomain.
 * The tenantId from the JWT is used directly as the subdomain.
 * Override specific mappings here if tenantId differs from subdomain slug.
 */
function getTenantSubdomain(tenantId: string): string | null {
  // Explicit overrides (tenantId -> subdomain slug)
  const tenantSubdomainMap: Record<string, string> = {
    'legacy': 'prodtestgym'
  };

  // Use explicit mapping if available, otherwise use tenantId directly as subdomain
  return tenantSubdomainMap[tenantId] ?? tenantId;
}

/**
 * Build the full subdomain URL for a given tenant.
 * Returns null if tenantId is invalid.
 */
export function getSubdomainUrl(tenantId: string): string | null {
  if (!isValidTenantId(tenantId)) return null
  const subdomain = getTenantSubdomain(tenantId)
  if (!subdomain) return null
  return `https://${subdomain}.${BASE_DOMAIN}`
}

/**
 * Redirect to tenant subdomain if available
 * 
 * @param tenantId - The tenant ID from the user's JWT token
 * @param token - The authentication token
 * @returns true if redirect was initiated, false otherwise
 */
export async function redirectToTenantSubdomain(
  tenantId: string | null,
  token: string
): Promise<boolean> {
  console.log('[REDIRECT] redirectToTenantSubdomain called with tenantId:', tenantId);
  
  // Skip if no tenant ID
  if (!tenantId) {
    console.log('[REDIRECT] No tenant ID, skipping subdomain redirect');
    return false;
  }

  // Skip if already on a subdomain
  if (!isOnMainDomain()) {
    console.log('[REDIRECT] Already on subdomain, skipping redirect');
    return false;
  }

  // Skip if redirect is disabled
  if (!isSubdomainRedirectEnabled()) {
    console.log('[REDIRECT] Subdomain redirect disabled, skipping');
    return false;
  }

  // Skip if tenant ID is not valid
  if (!isValidTenantId(tenantId)) {
    console.log('[REDIRECT] Invalid tenant ID format, skipping redirect');
    return false;
  }

  // Get subdomain from mapping
  const subdomain = getTenantSubdomain(tenantId);
  console.log('[REDIRECT] Mapped tenantId to subdomain:', subdomain);
  
  if (!subdomain) {
    console.log('[REDIRECT] No subdomain mapping found for tenant:', tenantId);
    return false;
  }

  // Build subdomain URL
  const subdomainUrl = `https://${subdomain}.${BASE_DOMAIN}`;
  const currentPath = window.location.pathname + window.location.search + window.location.hash;
  const redirectUrl = `${subdomainUrl}${currentPath}`;
  
  console.log('[REDIRECT] Redirecting to tenant subdomain:', redirectUrl);
  
  // Use replace to avoid back button issues
  window.location.replace(redirectUrl);
  
  return true;
}

/**
 * Disable subdomain redirect (for testing/debugging)
 */
export function disableSubdomainRedirect(): void {
  localStorage.setItem('disableSubdomainRedirect', 'true');
  logger.debug('Subdomain redirect disabled');
}

/**
 * Enable subdomain redirect
 */
export function enableSubdomainRedirect(): void {
  localStorage.removeItem('disableSubdomainRedirect');
  logger.debug('Subdomain redirect enabled');
}
